import type { ReactiveController, ReactiveControllerHost } from 'lit'
import { Subject, merge, takeUntil } from 'rxjs'

import { fromResizeObserver } from '@schmancy/directives/layout'

/**
 * The field's own inline size, in px, below which an action collapses to its
 * icon. Read off the FIELD, never the viewport: the same field sits in a wide
 * two-column form and in a 320px sheet, and only its own box knows which. At
 * 360px a field carrying an expanded action ("translate" glyph + label + the
 * ladder's gap and padding, ~130px) has under 230px left for the value it
 * exists to show, which is where the action stops being furniture on the field
 * and starts being furniture in front of it.
 */
export const FIELD_ACTIONS_COMPACT_WIDTH = 360

/**
 * Whether one node carries something the accessibility tree can compute a name
 * from. `ariaLabel` is read as the IDL property beside the attribute, because a
 * control that moves its name onto the node carrying the role strips the host
 * attribute in the same act — `<schmancy-icon-button>` does exactly that, and
 * `host.getAttribute('aria-label')` reads null on one that is correctly named.
 */
function carriesName(node: Element): boolean {
	return (
		node.hasAttribute('aria-label') ||
		(node.ariaLabel ?? '').trim() !== '' ||
		node.hasAttribute('aria-labelledby') ||
		node.hasAttribute('title') ||
		(node.textContent ?? '').trim() !== ''
	)
}

/**
 * The `actions` slot's shared half — everything two field primitives with
 * different geometry still owe identically: which controls are in the slot, how
 * wide they render, whether the field is narrow enough to collapse them, and
 * pushing that verdict onto the controls themselves.
 *
 * It is a controller rather than a mixin method because `FormFieldMixin` renders
 * nothing — the slot's markup is per-field by necessity (a single-line field
 * pins it inside the trailing edge, a multi-line one gives it a row of its own),
 * and only what surrounds that markup is shared. It is also opt-in per field, so
 * a checkbox or a switch that never renders the slot pays for no
 * `ResizeObserver`.
 *
 * `compact` reaches slotted controls as an ATTRIBUTE on the consumer's own
 * elements. That is a deliberate reach into light DOM: a field's shadow CSS
 * cannot select the label inside a slotted control's own shadow root, so the
 * responsive contract can only be delivered by telling the control, and a
 * control that does not understand `compact` simply keeps its label — never
 * loses its name.
 */
export class FieldActionsController implements ReactiveController {
	/** The field is narrow enough that its actions render icon-only. */
	compact = false

	/** Rendered inline size of the actions region, px — 0 while the slot is empty. */
	width = 0

	private assigned: HTMLElement[] = []
	private region?: Element
	private readonly stopObserving = new Subject<void>()

	constructor(private readonly host: ReactiveControllerHost & HTMLElement) {
		host.addController(this)
	}

	hostDisconnected(): void {
		this.stopObserving.next()
	}

	/** True once the consumer has put something in the slot. */
	get filled(): boolean {
		return this.assigned.length > 0
	}

	/**
	 * `ref` callback for the element wrapping the slot. Bound as an arrow field so
	 * the binding keeps one reference across renders — Lit's `ref` re-invokes a
	 * changed callback with `undefined` and then the element, which would
	 * re-subscribe the observers on every single render.
	 */
	readonly observe = (el?: Element): void => {
		this.stopObserving.next()
		this.region = el
		if (!el) {
			this.width = 0
			return
		}
		// Both boxes matter and neither implies the other: the host's width decides
		// `compact`, the region's decides how much trailing room the field's own
		// text has to give up. One pipeline, one subscribe, torn down by the
		// Subject above on disconnect and on re-attach.
		//
		// The subscribe IS the first measurement: a ResizeObserver delivers an
		// initial observation for every element it starts observing. A synchronous
		// `measure()` here would take that same reading one commit earlier — from
		// inside the render this `ref` callback runs in — where a forced layout is
		// charged to every field that mounts and where the `requestUpdate` a
		// changed reading earns lands inside the update that is still running,
		// which is exactly Lit's `change-in-update` warning.
		merge(fromResizeObserver(this.host), fromResizeObserver(el))
			.pipe(takeUntil(this.stopObserving))
			.subscribe(() => this.measure())
	}

	/** `@slotchange` handler for the `actions` slot. Bound as an arrow field for the same reason as `observe`. */
	readonly sync = (e: Event): void => {
		const slot = e.target
		if (!(slot instanceof HTMLSlotElement)) return
		const wasFilled = this.filled
		this.assigned = slot
			.assignedElements({ flatten: true })
			.filter((node): node is HTMLElement => node instanceof HTMLElement)
		if (import.meta.env.DEV) {
			for (const el of this.assigned) {
				// A control that collapses to a glyph with no name is nameless the
				// moment its label stops rendering, and nothing downstream says so.
				// Resolved off BOTH the slotted host and the node one shadow root in
				// that carries the role (`[part="base"]`), because a component is free
				// to move the name a consumer wrote on its host down to that node —
				// and the two together also straddle upgrade: before it the attribute
				// is still on the host, after it the name is on the property.
				const roleNode = el.shadowRoot?.querySelector('[part="base"]') ?? el
				if (carriesName(el) || carriesName(roleNode)) continue
				console.warn(
					'[schmancy] a control in the `actions` slot has no accessible name — it collapses to an icon on a narrow field and would announce as nothing. Give it aria-label, or text content the control keeps as its name when collapsed.',
					el,
				)
			}
		}
		this.publishCompact()
		this.measure()
		// Only the filled/empty flip changes what the host renders; a slot whose
		// contents were swapped for others renders the same box, and `measure`
		// already asks for the update the new width earns.
		if (wasFilled !== this.filled) this.host.requestUpdate()
	}

	private measure(): void {
		const compact = this.host.getBoundingClientRect().width < FIELD_ACTIONS_COMPACT_WIDTH
		const width = this.filled ? (this.region?.getBoundingClientRect().width ?? 0) : 0
		if (compact !== this.compact) {
			this.compact = compact
			this.publishCompact()
		}
		// `width` is the only half the host itself renders — it is the trailing
		// room the field's text gives up. `compact` is delivered to the slotted
		// controls as an attribute and to nothing else, so a field whose slot is
		// empty (width fixed at 0) re-renders for no reading this controller
		// takes, however narrow the field is.
		if (width === this.width) return
		this.width = width
		this.host.requestUpdate()
	}

	private publishCompact(): void {
		for (const el of this.assigned) el.toggleAttribute('compact', this.compact)
	}
}
